import React from 'react'
import classnames from 'classnames'
import { compose } from 'redux'
import {
  makeForm,
  TextFormField,
  SelectDropdownFormField,
  MaskedFormField,
  BaseComponent
} from 'belle-js'
import Analytics from 'analytics'
import styles from 'components/Onboarding/FormStyles.module.scss'
import {
  countryList,
  identificationTypes
} from 'utils/fieldOptions'
import {
  identificationNumberValidation,
  ssnValidation,
  ssnConfirmationValidation,
} from 'utils/fieldValidations'
import {
  SSN_TYPE,
  ID_TYPE,
  MASK_CHAR_NUMBER,
  MASK_PATTERN, 
} from 'utils/fieldMasking'
import withFormResponseHandler from '../withFormResponseHandler'

class IdentityVerificationForm extends BaseComponent {

  constructor (props) {
    super(props)
    this.state = {
      wasSsnEdited: false,
      wasIDEdited: false,
    }
    this.bind('handleFocus', 'handleBlur')
  }

  handleBlur (type, isMasked) {
    if (isMasked) return

    const { formData } = this.props
    let newState = {}

    if (type === SSN_TYPE && formData.ssn === '') {
      newState = {
        wasSsnEdited: false
      }
    } else if (type === ID_TYPE && formData.identificationNumber === '') {
      newState = {
        wasIDEdited: false
      }
    }
    this.setState(newState)
  }

  handleFocus (type, isMasked) {
    if (!isMasked) return

    let newState = {}
    if (type === SSN_TYPE) {
      newState = {
        wasSsnEdited: true
      }
    } else  if (type === ID_TYPE) {
      newState = {
        wasIDEdited: true
      }
    }
    this.setState(newState)
  }

  getMaskedValue (value) {
    return value !== null ? value.padStart(value.length + MASK_CHAR_NUMBER, '*') : ''
  }

  render () {
    const {
      formValid,
      formData,
      initialData,
      onClick,
      isProspect,
    } = this.props

    const {
      wasSsnEdited,
    } = this.state

    const primaryButtonClasses = classnames(
      'button',
      'button--primary',
      styles.marginTop20,
      !formValid ? 'disabled' : null,
    )

    const {
      lastFourDigitSsn,
      lastFourDigitIdentificationNumber
    } = initialData

    const isSSNMasked = !this.state.wasSsnEdited && lastFourDigitSsn
    const isIdentificationMasked = !this.state.wasIDEdited && lastFourDigitIdentificationNumber

    let showIdentificationFields = false
    if (formData.primaryCitizenship !== 'United States') {
      showIdentificationFields = true
    }

    const twoColumnFormSection = classnames(
      styles.twoColumn,
      styles.formSection,
      styles.citizenship
    )

    const handleSubmitClick = () => {
      onClick()
      Analytics.track('User clicks Save and Continue Identity Verification')
    }

    const citizenshipCallout = `${isProspect ? 'If you have' : 'If this person has'} citizenship in more than one country, please select ${isProspect ? 'your' : 'their'} primary citizenship.`
    const newSsnAdded = (!initialData.ssn && !!formData.ssn)
    const ssnChange = wasSsnEdited || newSsnAdded
    return (
      <div className={styles.Form}>
        <div className={styles.formSection}>
          <p className={styles.heading}>Social Security Number</p>
          <div className={styles.twoColumn}>
            <MaskedFormField
              name="ssn"
              label="Social Security Number"
              placeholder="123-45-6789"
              className={styles.FormField}
              validations={isSSNMasked ? {} : { isValidSocialSecurityNumber: true }}
              validationErrors={ssnValidation.messages}     
              testId="identityVerificationForm__ssn__field"
              required={false}
              value={isSSNMasked ? lastFourDigitSsn : ''}
              mask={isSSNMasked ? MASK_PATTERN : 'ssn'}
              onBlur={() => this.handleBlur(SSN_TYPE, isSSNMasked)}
              onFocus={() => this.handleFocus(SSN_TYPE, isSSNMasked)} />

            {(wasSsnEdited || !lastFourDigitSsn) && <MaskedFormField
              name="ssnConfirmation"
              label="Social Security Number Confirmation"
              placeholder="Confirm SSN"
              className={styles.FormField}
              validations={ssnChange ? { isValidSsnMatch: true } : {}}
              mask={isSSNMasked ? MASK_PATTERN : 'ssn'}
              validationErrors={ssnConfirmationValidation.messages}
              testId="identityVerificationForm__ssnConfirmation__field"
              displayError={ssnChange}
              required={ssnChange} />}
          </div>
        </div>

        <div className={styles.formSection}>
          <p className={styles.heading}>Country of Legal Residence</p>
          <SelectDropdownFormField
            name="countryOfResidence"
            label="Country of Residence"
            placeholder="Country"
            className={styles.FormField}
            options={countryList}
            value={formData.countryOfResidence || 'United States'}
            testId="identityVerificationForm__countryOfResidence__field"
            required={false} />
        </div>
        <div className={twoColumnFormSection}>
          <div>
            <p className={styles.heading}>Country of Citizenship</p>
            <SelectDropdownFormField
              name="primaryCitizenship"
              label="Country of Citizenship"
              className={styles.FormField}
              placeholder="Country"
              options={countryList}
              value={formData.primaryCitizenship || 'United States'}
              testId="identityVerificationForm__primaryCitizenship__field"
              required={false} />
          </div>
          <div className={styles.callout}>
            {citizenshipCallout}
          </div>
        </div>
        <div className={styles.formSection}>
          <p className={styles.heading}>Country of Birth</p>
          <SelectDropdownFormField
            name="countryOfBirth"
            label="Country of Residence"
            className={styles.FormField}
            placeholder="Country"
            options={countryList}
            value={formData.countryOfBirth || 'United States'}
            testId="identityVerificationForm__countryOfBirth__field"
            required={false} />
        </div>

        {showIdentificationFields &&
        <div>
          <div className={styles.twoColumn}>
            <div className={styles.formSection}>
              <p className={styles.heading}>Identification Type</p>
              <SelectDropdownFormField
                name="identificationType"
                label="Identification Type"
                placeholder="Select Identification Type"
                className={styles.FormField}
                options={identificationTypes}
                value={formData.identificationType}
                testId="identityVerificationForm__identificationType__field"
                required={false} />
            </div>
            <div className={styles.formSection}>
              <p className={styles.heading}>Identification Number</p>
              <TextFormField
                name="identificationNumber"
                label="Identification Number"
                placeholder="123-45-6789"
                className={styles.FormField}
                maxLength={32}
                validations={isIdentificationMasked ?
                  undefined : identificationNumberValidation.rules
                }
                validationErrors={
                  isIdentificationMasked ? undefined : identificationNumberValidation.messages
                }
                value={
                  isIdentificationMasked ? this.getMaskedValue(lastFourDigitIdentificationNumber) : ''
                }
                testId="identityVerificationForm__identificationNumber__field"
                onBlur={isIdentificationMasked ?  undefined : this.handleBlur.bind(this, ID_TYPE)} // eslint-disable-line
                onFocus={isIdentificationMasked ? this.handleFocus.bind(this, ID_TYPE) : undefined} // eslint-disable-line
                required={false} />
            </div>
          </div>

          <div className={styles.formSection}>
            <p className={styles.heading}>State or Country of Issue</p>
            <TextFormField
              name="localeOfIdIssuance"
              label="localeOfIdIssuance"
              className={styles.FormField}
              maxLength={50}
              placeholder="State or Country of Issue"
              validations={{ isValidEntityName: true }}
              validationErrors={{ isValidEntityName: 'Hmm, that’s not a valid state or country.' }}
              value={formData.localeOfIdIssuance || ''}
              testId="identityVerificationForm__localeOfIdIssuance__field"
              required={false} />
          </div>

          <div className={styles.twoColumn}>
            <div className={styles.formSection}>
              <p className={styles.heading}>Issue Date</p>
              <MaskedFormField
                name="identificationIssueDate"
                label="Identification Issue Date"
                value={formData.identificationIssueDate}
                className={styles.FormField}
                mask="date"
                placeholder="MM/DD/YYYY"
                validations={{ isValidStartingDate: true, isValidFutureDate: true }}
                validationErrors={{
                  isValidStartingDate: 'Hmm, that’s not a valid date.',
                  isValidFutureDate: 'That date is too far in the future.'
                }}
                validationError="Please enter a valid date."
                testId="identityVerificationForm__identificationIssueDate__field"
                required={false} />
            </div>
            <div className={styles.formSection}>
              <p className={styles.heading}>Expiration Date</p>
              <MaskedFormField
                name="identificationExpirationDate"
                label="Identification Expiration Date"
                value={formData.identificationExpirationDate}
                className={styles.FormField}
                mask="date"
                placeholder="MM/DD/YYYY"
                validations={{
                  isValidExpirationDate: true,
                  isValidFutureDate: true
                }}
                validationErrors={{
                  isValidExpirationDate: 'Hmm, that’s not a valid date.',
                  isValidFutureDate: 'That date is too far in the future.'
                }}
                testId="identityVerificationForm__identificationExpirationDate__field"
                required={false} />
            </div>
          </div>
        </div>}
        {
          /* eslint-disable react/button-has-type */
          <button
            className={primaryButtonClasses}
            type={formValid ? 'submit' : 'button'}
            data-test-id="identityVerificationForm__saveAndContinue__cta"
            onClick={handleSubmitClick}>
            Save and Continue
          </button>
        }
      </div>
    )
  }

}

export default compose(
  makeForm,
  withFormResponseHandler,
)(IdentityVerificationForm)



// WEBPACK FOOTER //
// ./src/components/Onboarding/IdentityVerification/IdentityVerificationForm.jsx